
export interface PasswordRequirement {
  id: string;
  label: string;
  test: (password: string) => boolean;
  met: boolean;
}

export interface PasswordStrengthResult {
  score: number;
  level: 'Very Weak' | 'Weak' | 'Fair' | 'Good' | 'Strong';
  requirements: PasswordRequirement[];
  isValid: boolean;
}

const commonPasswords = [
  'password', '123456', '123456789', 'qwerty', 'abc123', 'password123',
  'admin', 'letmein', 'welcome', 'monkey', '1234567890', 'password1'
];

const sequentialPatterns = [
  '123', '234', '345', '456', '567', '678', '789', '890',
  'abc', 'bcd', 'cde', 'def', 'efg', 'fgh', 'ghi', 'hij'
];

export const checkPasswordStrength = (password: string): PasswordStrengthResult => {
  const requirements: PasswordRequirement[] = [
    {
      id: 'length',
      label: 'At least 12 characters',
      test: (pwd) => pwd.length >= 12,
      met: false
    },
    {
      id: 'uppercase',
      label: 'One uppercase letter',
      test: (pwd) => /[A-Z]/.test(pwd),
      met: false
    },
    {
      id: 'lowercase',
      label: 'One lowercase letter',
      test: (pwd) => /[a-z]/.test(pwd),
      met: false
    },
    {
      id: 'number',
      label: 'One number',
      test: (pwd) => /[0-9]/.test(pwd),
      met: false
    },
    {
      id: 'special',
      label: 'One special character (!@#$%^&*)',
      test: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
      met: false
    },
    {
      id: 'common',
      label: 'Not a common password',
      test: (pwd) => !commonPasswords.includes(pwd.toLowerCase()),
      met: false
    },
    {
      id: 'sequential',
      label: 'No sequential characters',
      test: (pwd) => !sequentialPatterns.some(pattern => 
        pwd.toLowerCase().includes(pattern) || pwd.includes(pattern)
      ),
      met: false
    }
  ];

  // Check each requirement
  requirements.forEach(req => {
    req.met = req.test(password);
  });

  const metCount = requirements.filter(req => req.met).length;
  const score = Math.round((metCount / requirements.length) * 100);

  let level: PasswordStrengthResult['level'];
  if (score < 30) level = 'Very Weak';
  else if (score < 50) level = 'Weak';
  else if (score < 70) level = 'Fair';
  else if (score < 85) level = 'Good';
  else level = 'Strong';

  const isValid = level === 'Strong' && metCount === requirements.length;

  return {
    score,
    level,
    requirements,
    isValid
  };
};

export const getStrengthColor = (level: string): string => {
  switch (level) {
    case 'Very Weak': return 'bg-red-500';
    case 'Weak': return 'bg-red-400';
    case 'Fair': return 'bg-yellow-500';
    case 'Good': return 'bg-blue-500';
    case 'Strong': return 'bg-green-500';
    default: return 'bg-gray-300';
  }
};

export const getStrengthTextColor = (level: string): string => {
  switch (level) {
    case 'Very Weak': return 'text-red-600';
    case 'Weak': return 'text-red-500';
    case 'Fair': return 'text-yellow-600';
    case 'Good': return 'text-blue-600';
    case 'Strong': return 'text-green-600';
    default: return 'text-gray-500';
  }
};
